import cx from 'clsx';

import { GroupSection } from '../group-section';
import {
  SPEC_ITEM_TITLE_AC_CHARGE_RATE,
  SPEC_ITEM_TITLE_AC_CHARGE_TIME,
  SPEC_ITEM_TITLE_ACCELERATION,
  SPEC_ITEM_TITLE_BATTERY_CAPACITY,
  SPEC_ITEM_TITLE_BATTERY_TYPE,
  SPEC_ITEM_TITLE_BOOT_CARGO_CAPACITY,
  SPEC_ITEM_TITLE_BRAKES_FRONT,
  SPEC_ITEM_TITLE_BRAKES_REAR,
  SPEC_ITEM_TITLE_CO2_EMISSION,
  SPEC_ITEM_TITLE_CO2_EMISSION_LTA,
  SPEC_ITEM_TITLE_COMBINED_POWER,
  SPEC_ITEM_TITLE_DC_CHARGE_RATE,
  SPEC_ITEM_TITLE_DC_CHARGE_TIME,
  SPEC_ITEM_TITLE_DIMENSIONS,
  SPEC_ITEM_TITLE_DRIVE_RANGE,
  SPEC_ITEM_TITLE_DRIVE_TYPE,
  SPEC_ITEM_TITLE_ENERGY_CONSUMPTION,
  SPEC_ITEM_TITLE_ENGINE_CAPACITY,
  SPEC_ITEM_TITLE_ENGINE_POWER,
  SPEC_ITEM_TITLE_ENGINE_TYPE,
  SPEC_ITEM_TITLE_FUEL_CONSUMPTION,
  SPEC_ITEM_TITLE_FUEL_TANK_CAPACITY,
  SPEC_ITEM_TITLE_FUEL_TYPE,
  SPEC_ITEM_TITLE_KERB_WEIGHT,
  SPEC_ITEM_TITLE_MIN_TURNING_RADUIS,
  SPEC_ITEM_TITLE_MOTOR_POWER,
  SPEC_ITEM_TITLE_POWER,
  SPEC_ITEM_TITLE_RIM_SIZE,
  SPEC_ITEM_TITLE_SEATING_CAPACITY,
  SPEC_ITEM_TITLE_SUSPENSION_FRONT,
  SPEC_ITEM_TITLE_SUSPENSION_REAR,
  SPEC_ITEM_TITLE_TOP_SPEED,
  SPEC_ITEM_TITLE_TORQUE,
  SPEC_ITEM_TITLE_TRANSMISSION,
  SPEC_ITEM_TITLE_VEHICLE_TYPE,
  SPEC_ITEM_TITLE_WHEELBASE,
  SPEC_SUB_TITLE_ELECTRIC_MOTOR,
  SPEC_SUB_TITLE_ENGINE_AND_TRANSMISSION,
  SPEC_SUB_TITLE_MEASUREMENT,
  SPEC_SUB_TITLE_PERFORMANCE,
  SPEC_SUB_TITLE_SUSPENSION_BRAKES_WHEELS,
} from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';

export const SpecsInfoTable: Component = ({
  vehicleType,
  submodel,
}) => {
  const { specifications } = submodel;

  const isElectriCar = specifications.fuelType === 'electric';
  const isHybrid = specifications.fuelType === 'diesel-electric'
    || specifications.fuelType === 'petrol-electric';

  const convertValueToText = (value: string | number | null, unit?: string) => {
    if (value === null || value === 0) {
      return 'unknown';
    }

    if (unit) {
      if (typeof value === 'number') {
        const formattedValue = value.toLocaleString();
        return `${formattedValue} ${unit}`;
      }
      return `${value} ${unit}`;
    }

    return `${value}`;
  };

  const groupedSpecs = [
    {
      title: SPEC_SUB_TITLE_ENGINE_AND_TRANSMISSION,
      items: [
        { title: SPEC_ITEM_TITLE_ENGINE_CAPACITY, value: convertValueToText(specifications.engineCapacity, 'cc'), show: !isElectriCar }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_ENGINE_TYPE, value: convertValueToText(specifications.engineType), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_BATTERY_TYPE, value: convertValueToText(specifications.batteryType), show: isElectriCar || isHybrid }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_FUEL_TYPE, value: convertValueToText(specifications.fuelType && `${specifications.fuelType.charAt(0).toUpperCase()}${specifications.fuelType.slice(1)}`), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_DRIVE_TYPE, value: convertValueToText(specifications.driveType), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_TRANSMISSION, value: convertValueToText(specifications.transmission), show: true }, // @suppress longLineCheck
      ],
    },
    {
      title: SPEC_SUB_TITLE_PERFORMANCE,
      items: [
        { title: SPEC_ITEM_TITLE_POWER, value: submodel.power ? convertValueToText(Math.round(submodel.power * 0.7456998716), `kW (${submodel.power} bhp)`) : 'unknown', show: !isHybrid }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_ENGINE_POWER, value: submodel.powerEngine ? convertValueToText(Math.round(submodel.powerEngine * 0.7456998716), `kW (${submodel.powerEngine} bhp)`) : 'unknown', show: isHybrid }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_MOTOR_POWER, value: submodel.powerMotor ? convertValueToText(Math.round(submodel.powerMotor * 0.7456998716), `kW (${submodel.powerMotor} bhp)`) : 'unknown', show: isHybrid }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_COMBINED_POWER, value: submodel.powerCombined ? convertValueToText(Math.round(submodel.powerCombined * 0.7456998716), `kW (${submodel.powerCombined} bhp)`) : 'unknown', show: isHybrid }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_TORQUE, value: convertValueToText(specifications.torque, 'Nm'), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_ACCELERATION, value: convertValueToText(specifications.acceleration, 's (0-100 km/h)'), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_TOP_SPEED, value: convertValueToText(specifications.topSpeed, 'km/h'), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_FUEL_CONSUMPTION, value: specifications.fuelConsumption, show: !isElectriCar }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_CO2_EMISSION, value: convertValueToText(specifications.co2Emission, 'g/km (Manufacturer specs)'), show: !!specifications.co2Emission && !specifications.co2EmissionLta }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_CO2_EMISSION_LTA, value: convertValueToText(specifications.co2EmissionLta, 'g/km g/km (As tested by LTA)'), show: !!specifications.co2EmissionLta }, // @suppress longLineCheck
      ],
    },
    {
      title: SPEC_SUB_TITLE_MEASUREMENT,
      items: [
        { title: SPEC_ITEM_TITLE_VEHICLE_TYPE, value: vehicleType, show: !!vehicleType }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_SEATING_CAPACITY, value: convertValueToText(specifications.seatingCapacity), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_DIMENSIONS, value: convertValueToText(specifications.dimensions, 'mm'), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_WHEELBASE, value: convertValueToText(specifications.wheelbase, 'mm'), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_MIN_TURNING_RADUIS, value: convertValueToText(specifications.minTurningRadius, 'mm'), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_KERB_WEIGHT, value: convertValueToText(specifications.kerbWeight, 'kg'), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_FUEL_TANK_CAPACITY, value: convertValueToText(specifications.fuelTankCapacity, 'L'), show: !isElectriCar }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_BOOT_CARGO_CAPACITY, value: convertValueToText(specifications.cargoCapacity, 'L'), show: true }, // @suppress longLineCheck
      ],
    },
    {
      title: SPEC_SUB_TITLE_ELECTRIC_MOTOR,
      items: [
        { title: SPEC_ITEM_TITLE_BATTERY_CAPACITY, value: convertValueToText(specifications.batteryCapacity, 'kWh'), show: isElectriCar && !!specifications.batteryCapacity }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_DRIVE_RANGE, value: convertValueToText(specifications.driveRange, 'km'), show: isElectriCar && !!specifications.driveRange }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_ENERGY_CONSUMPTION, value: specifications.fuelConsumption, show: isElectriCar && !!specifications.fuelConsumption }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_AC_CHARGE_RATE, value: convertValueToText(specifications.acChargingRate, 'kW'), show: isElectriCar && !!specifications.acChargingRate }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_AC_CHARGE_TIME, value: convertValueToText(specifications.acChargingTime, 'h'), show: isElectriCar && !!specifications.acChargingTime }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_DC_CHARGE_RATE, value: convertValueToText(specifications.dcChargingRate, 'kW'), show: isElectriCar && !!specifications.dcChargingRate }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_DC_CHARGE_TIME, value: convertValueToText(specifications.dcChargingTime, 'h'), show: isElectriCar && !!specifications.dcChargingTime }, // @suppress longLineCheck
      ],
    },
    {
      title: SPEC_SUB_TITLE_SUSPENSION_BRAKES_WHEELS,
      items: [
        { title: SPEC_ITEM_TITLE_SUSPENSION_FRONT, value: convertValueToText(specifications.suspensionFront), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_SUSPENSION_REAR, value: convertValueToText(specifications.suspensionRear), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_BRAKES_FRONT, value: convertValueToText(specifications.breakFront), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_BRAKES_REAR, value: convertValueToText(specifications.breakRear), show: true }, // @suppress longLineCheck
        { title: SPEC_ITEM_TITLE_RIM_SIZE, value: convertValueToText(specifications.rimSize, '"'), show: true }, // @suppress longLineCheck
      ],
    },
  ];
  return (
    <div className={cx(
      styles.containerRoot,
    )}>
      {groupedSpecs.map(group => (
        <GroupSection
          key={group.title}
          items={group.items}
          title={group.title} />
      ))}
    </div>
  );
};
